// src/app/shared/ui/WelcomeModal.tsx
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bot, Check, Gift, Image as ImageIcon, MessageSquare, Sparkles, Video, Zap, X } from "lucide-react";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";

interface WelcomeModalProps {
  enabled: boolean;
}

const STORAGE_KEY = "hasSeenWelcome";

export function WelcomeModal({ enabled }: WelcomeModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
      return;
    }

    const hasSeenWelcome = localStorage.getItem(STORAGE_KEY);
    if (!hasSeenWelcome) {
      setIsOpen(true);
    }
  }, [enabled]);

  const handleNext = () => {
    if (step < 4) {
      setStep((prev) => prev + 1);
    }
  };

  const handleFinish = (route?: string) => {
    localStorage.setItem(STORAGE_KEY, "true");
    setIsOpen(false);
    if (route) {
      navigate(route);
    }
  };

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setIsOpen(false);
  };

  if (!isOpen || !enabled) {
    return null;
  }

  const slides = [
    {
      id: 1,
      title: t('welcomeTitle1'),
      description: t('welcomeDesc1'),
      icon: Bot,
      gradient: "from-violet-500 to-indigo-600",
      glow: "bg-violet-500/30",
    },
    {
      id: 2,
      title: t('welcomeTitle2'),
      description: t('welcomeDesc2'),
      icon: Zap,
      gradient: "from-fuchsia-500 to-purple-600",
      glow: "bg-fuchsia-500/30",
    },
    {
      id: 3,
      title: t('welcomeTitle3'),
      description: t('welcomeDesc3'),
      icon: Gift,
      gradient: "from-amber-400 to-orange-500",
      glow: "bg-amber-500/30",
    },
    {
      id: 4,
      title: t('welcomeTitle4'),
      description: t('welcomeDesc4'),
    },
  ];

  const currentSlide = slides[step - 1];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative flex max-h-[90vh] min-h-[520px] w-full max-w-sm flex-col overflow-hidden rounded-[2rem] border border-white/20 dark:border-white/10 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl shadow-2xl"
      >
        {/* Кнопка закрытия */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/60 dark:bg-white/10 text-foreground/70 hover:text-foreground hover:bg-white/80 dark:hover:bg-white/20 transition-colors"
          aria-label={t('close')}
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="flex flex-1 flex-col"
          >
            {step < 4 ? (
              <div className="flex h-full flex-col">
                <div className="relative flex h-56 w-full items-center justify-center overflow-hidden bg-gradient-to-br from-violet-500/5 to-indigo-500/5 dark:from-violet-500/10 dark:to-indigo-500/10">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, repeat: Infinity, repeatType: "reverse" }}
                    className={`absolute h-32 w-32 rounded-full blur-3xl ${currentSlide.glow}`}
                  />

                  <motion.div
                    initial={{ scale: 0, rotate: -10 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", bounce: 0.5, delay: 0.1 }}
                    className={`relative z-10 flex h-24 w-24 items-center justify-center rounded-3xl bg-gradient-to-br ${currentSlide.gradient} text-white shadow-xl shadow-violet-500/30`}
                  >
                    {currentSlide.icon && <currentSlide.icon className="h-12 w-12" strokeWidth={1.5} />}

                    {step === 3 ? (
                      <motion.div
                        initial={{ scale: 0, y: 10 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ delay: 0.4, type: "spring" }}
                        className="absolute -bottom-3 -right-3 flex items-center gap-1 rounded-full border border-border bg-white dark:bg-gray-900 px-3 py-1 shadow-lg"
                      >
                        <span className="text-sm font-bold text-amber-500">+100</span>
                      </motion.div>
                    ) : null}
                  </motion.div>
                </div>

                <div className="flex flex-1 flex-col justify-center p-8 text-center">
                  <h2 className="mb-3 text-2xl font-bold tracking-tight text-foreground">
                    {currentSlide.title}
                  </h2>
                  <p className="text-[15px] leading-relaxed text-muted-foreground">
                    {currentSlide.description}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex h-full flex-col justify-center p-8">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", bounce: 0.5 }}
                  className="mx-auto mb-6 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 shadow-lg shadow-violet-500/30"
                >
                  <Sparkles className="h-8 w-8 text-white" strokeWidth={1.5} />
                </motion.div>
                <h2 className="mb-3 text-center text-2xl font-bold tracking-tight text-foreground">
                  {currentSlide.title}
                </h2>
                <p className="mb-8 text-center text-[15px] text-muted-foreground">
                  {currentSlide.description}
                </p>

                <div className="w-full space-y-3">
                  <button
                    onClick={() => handleFinish("/text-generation")}
                    className="group flex w-full items-center rounded-2xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-white/5 p-4 transition-all duration-200 hover:border-violet-400/40 hover:bg-violet-500/5 active:scale-[0.98]"
                  >
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white transition-transform duration-300 group-hover:scale-110 shadow-md shadow-violet-500/20">
                      <MessageSquare className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-[15px] font-semibold text-foreground">{t('welcomeText')}</div>
                      <div className="mt-0.5 text-[13px] text-muted-foreground">{t('welcomeTextDesc')}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground/50 transition-all group-hover:translate-x-1 group-hover:text-violet-500" />
                  </button>

                  <button
                    onClick={() => handleFinish("/")}
                    className="group flex w-full items-center rounded-2xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-white/5 p-4 transition-all duration-200 hover:border-violet-400/40 hover:bg-violet-500/5 active:scale-[0.98]"
                  >
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white transition-transform duration-300 group-hover:scale-110 shadow-md shadow-fuchsia-500/20">
                      <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-[15px] font-semibold text-foreground">{t('welcomeImage')}</div>
                      <div className="mt-0.5 text-[13px] text-muted-foreground">{t('welcomeImageDesc')}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground/50 transition-all group-hover:translate-x-1 group-hover:text-violet-500" />
                  </button>

                  <button
                    onClick={() => handleFinish("/")}
                    className="group flex w-full items-center rounded-2xl border border-white/20 dark:border-white/10 bg-white/60 dark:bg-white/5 p-4 transition-all duration-200 hover:border-violet-400/40 hover:bg-violet-500/5 active:scale-[0.98]"
                  >
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 text-white transition-transform duration-300 group-hover:scale-110 shadow-md shadow-pink-500/20">
                      <Video className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-[15px] font-semibold text-foreground">{t('welcomeVideo')}</div>
                      <div className="mt-0.5 text-[13px] text-muted-foreground">{t('welcomeVideoDesc')}</div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-muted-foreground/50 transition-all group-hover:translate-x-1 group-hover:text-violet-500" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step < 4 ? (
          <div className="mt-auto p-8 pt-0">
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((index) => (
                  <div
                    key={index}
                    className={`h-1.5 rounded-full transition-all duration-500 ease-out ${
                      index === step
                        ? "w-8 bg-gradient-to-r from-violet-500 to-indigo-600"
                        : index < step
                          ? "w-2 bg-violet-500/40"
                          : "w-2 bg-muted-foreground/20"
                    }`}
                  />
                ))}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNext}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg shadow-violet-500/30 transition-all duration-200"
              >
                {step === 3 ? <Check className="h-6 w-6" strokeWidth={2} /> : <ArrowRight className="h-6 w-6" strokeWidth={2} />}
              </motion.button>
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}